import dynamic from "next/dynamic"
import { type ISbStoryData, useStoryblokState } from "@storyblok/react"
import { ConditionalWrapper } from "@/components/atoms"
import { logger, LoggerWarningEventType } from "@/lib/logger/logger"
import { type SbContentStory } from "../../types"
import { hasBlokInContent } from "../../utils/hasBlokInContent"
import { BlogCategoryTemplate } from "./templates/BlogCategory"
import { BlogPostTemplate } from "./templates/BlogPost"
import { FaqArticleTemplate } from "./templates/FaqArticle"
import { FaqArticleB2bgTemplate } from "./templates/FaqArticleB2bg"
import { FaqCategoryTemplate } from "./templates/FaqCategory"
import { FaqCategoryB2bgTemplate } from "./templates/FaqCategoryB2bg"
import { HomeTemplate } from "./templates/Home"
import { LandingTemplate } from "./templates/Landing"
import { MeterPowerAnalysisTemplate } from "./templates/MeterPowerAnalysis"
import { PageTemplate } from "./templates/Page"

const AudioProvider = dynamic(() =>
    import("../../context/AudioContext").then((mod) => mod.AudioProvider)
)

function getTemplateFromContent(dynamicStory: ISbStoryData<SbContentStory["content"]>) {
    const { content } = dynamicStory

    switch (content.component) {
        case "blogCategory":
            return <BlogCategoryTemplate story={{ ...dynamicStory, content }} />
        case "blogPost":
            return <BlogPostTemplate story={{ ...dynamicStory, content }} />
        case "faqArticleB2bg":
            return <FaqArticleB2bgTemplate story={{ ...dynamicStory, content }} />
        case "faqCategoryB2bg":
            return <FaqCategoryB2bgTemplate story={{ ...dynamicStory, content }} />
        case "faqArticle":
            return <FaqArticleTemplate story={{ ...dynamicStory, content }} />
        case "faqCategory":
            return <FaqCategoryTemplate story={{ ...dynamicStory, content }} />
        case "meterPowerAnalysis":
            return <MeterPowerAnalysisTemplate story={{ ...dynamicStory, content }} />
        case "homePage":
            return <HomeTemplate story={{ ...dynamicStory, content }} />
        case "landing":
            return <LandingTemplate story={{ ...dynamicStory, content }} />
        case "page":
            return <PageTemplate story={{ ...dynamicStory, content }} />
        default:
            // Unmapped CMS component would otherwise render a silently blank page.
            logger.warning({
                eventType: LoggerWarningEventType.MissingData,
                description: `No template mapped for Storyblok content.component "${content.component}"`,
                meta: {
                    source: "StoryTemplate",
                },
                extra: {
                    slug: dynamicStory.full_slug,
                    component: content.component,
                    storyId: dynamicStory.id,
                },
            })

            return null
    }
}

type StoryProps = {
    story: SbContentStory
    preview?: boolean
}

export function StoryTemplate({ story }: StoryProps) {
    const dynamicStory = useStoryblokState<SbContentStory["content"]>(story)
    if (!dynamicStory) return null

    const template = getTemplateFromContent(dynamicStory)
    if (!template) return null

    return (
        <ConditionalWrapper
            condition={hasBlokInContent(dynamicStory.content, "audioIllustrated")}
            Wrapper={AudioProvider}
        >
            {template}
        </ConditionalWrapper>
    )
}
